/**
 * useKgFullscreen
 *
 * Binds the browser Fullscreen API to the canvas wrapper element.
 *   - wrapperRef  : attach to the element you want to go fullscreen.
 *   - isFullscreen: true while the element is the active fullscreen element.
 *   - toggle      : enters or exits fullscreen. Safe to call at any time.
 *
 * Listens to the `fullscreenchange` event so `isFullscreen` stays in sync
 * even when the user exits via the Esc key (browser-native).
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface UseKgFullscreenResult {
  wrapperRef: React.RefObject<HTMLDivElement>;
  isFullscreen: boolean;
  handleToggleFullscreen: () => void;
}

export const useKgFullscreen = (): UseKgFullscreenResult => {
  const wrapperRef                        = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen]   = useState(false);

  useEffect(() => {
    const sync = () =>
      setIsFullscreen(document.fullscreenElement === wrapperRef.current);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      wrapperRef.current?.requestFullscreen().catch(() => {});
    }
  }, []);

  return { wrapperRef, isFullscreen, handleToggleFullscreen };
};
